import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { QUERY_GET_LIST_VALUES } from 'graphql/queries/customLists';
import { NotificationCondition, OperatorType, FieldOption } from '../types';
import { useOperatorsByType } from './useNotificationsSettings';
import { FindListValues, FindListValuesVariables, ListValue } from 'views/backoffice/CustomLists/types';

interface UseConditionSectionParams {
    fields: FieldOption[];
    loadingFields: boolean;
    condition?: NotificationCondition;
    onChange: (condition: NotificationCondition | undefined) => void;
}

export const useConditionSection = ({ fields, loadingFields, condition, onChange }: UseConditionSectionParams) => {
    const [selectedField, setSelectedField] = useState<string>(condition?.fieldId || '');
    const [selectedOperator, setSelectedOperator] = useState<OperatorType | ''>(condition?.operator || '');
    const [value, setValue] = useState<string>(condition?.value?.toString() || '');
    const [listOptions, setListOptions] = useState<ListValue[]>([]);

    const currentField = fields.find((f) => f.id === selectedField);
    const { operators, loading: loadingOperators } = useOperatorsByType(currentField?.dataType || null);

    const listTypeId = currentField?.listType?.id ? Number(currentField.listType.id) : null;
    const isListField =
        currentField && ['dropdown', 'multiselect', 'single-select', 'multiselect-checkbox'].includes(currentField.dataType.toLowerCase());

    const [fetchListValues, { loading: loadingListValues }] = useLazyQuery<FindListValues, FindListValuesVariables>(QUERY_GET_LIST_VALUES, {
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            console.log('[Lists] 🟣 List Values Loaded:', {
                listTypeId,
                totalValues: data?.findListValues?.length || 0,
                enabledValues: data?.findListValues?.filter((v) => v.enabled).length || 0,
                values: data?.findListValues?.map((v) => ({ id: v.id, value: v.value, enabled: v.enabled }))
            });

            if (data?.findListValues) {
                setListOptions(data.findListValues.filter((v) => v.enabled));
            }
        },
        onError: (error) => {
            console.error('[Lists] ❌ Error loading list values:', {
                listTypeId,
                error: error.message
            });
        }
    });

    useEffect(() => {
        if (isListField && listTypeId) {
            console.log('[Lists] 🔶 Fetching List Values for Field:', {
                fieldId: selectedField,
                fieldName: currentField?.name,
                listTypeId,
                isListField
            });

            fetchListValues({
                variables: { data: { listIds: [listTypeId] } }
            });
        } else {
            setListOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listTypeId, isListField, fetchListValues]);

    useEffect(() => {
        if (condition) {
            setSelectedField(condition.fieldId);
            setSelectedOperator(condition.operator);
            setValue(condition.value?.toString() || '');
        } else if (!condition && selectedField) {
            setSelectedField('');
            setSelectedOperator('');
            setValue('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition]);

    useEffect(() => {
        const noValueOperators = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];
        const requiresValue = selectedOperator && !noValueOperators.includes(selectedOperator);
        const isValid = selectedField && selectedOperator && (!requiresValue || value);

        if (isValid) {
            const field = fields.find((f) => f.id === selectedField);
            if (field) {
                let parsedValue: string | number | boolean = value;

                if (requiresValue && value) {
                    const dataType = field.dataType.toLowerCase();

                    if (['number', 'integer', 'float', 'decimal', 'currency'].includes(dataType)) {
                        parsedValue = parseFloat(value) || 0;
                    } else if (['boolean', 'checkbox'].includes(dataType)) {
                        parsedValue = value === 'true';
                    } else if (['dropdown', 'multiselect', 'single-select', 'multiselect-checkbox'].includes(dataType)) {
                        parsedValue = value;
                    } else {
                        parsedValue = value;
                    }
                }

                const newCondition: NotificationCondition = {
                    fieldId: field.id,
                    fieldName: field.name,
                    fieldType: field.dataType,
                    operator: selectedOperator as OperatorType,
                    value: requiresValue ? parsedValue : ''
                };
                onChange(newCondition);
            }
        } else if (!selectedField) {
            onChange(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedField, selectedOperator, value]);

    const handleFieldChange = (fieldId: string) => {
        const field = fields.find(({ id }) => id === fieldId);

        console.log('🟢 Field Selected in Condition:', {
            fieldId,
            fieldName: field?.name,
            fieldDisplayName: field?.displayName,
            fieldDataType: field?.dataType,
            fieldCategory: field?.category,
            hasListType: !!field?.listType,
            listTypeId: field?.listType?.id,
            fullField: field
        });

        setSelectedField(fieldId);
        setSelectedOperator('');
        setValue('');
    };

    const handleOperatorChange = (operator: OperatorType) => {
        setSelectedOperator(operator);
        const noValueOperators = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];
        if (noValueOperators.includes(operator)) {
            setValue('');
        }
    };

    const handleClearField = () => {
        setSelectedField('');
        setSelectedOperator('');
        setValue('');
        onChange(undefined);
    };

    const handleClearOperator = () => {
        setSelectedOperator('');
        setValue('');
    };

    const handleClearValue = () => {
        setValue('');
    };

    const isValueRequired = () => {
        if (!selectedOperator) return false;
        const noValueOperators = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];
        return !noValueOperators.includes(selectedOperator);
    };

    const getInputType = () => {
        if (!currentField) return 'text';

        const dataType = currentField.dataType.toLowerCase();

        if (['dropdown', 'multiselect', 'single-select', 'multiselect-checkbox'].includes(dataType)) {
            return 'list';
        }

        if (['boolean', 'checkbox'].includes(dataType)) {
            return 'boolean';
        }

        if (dataType === 'phone') {
            return 'phone';
        }

        if (dataType === 'email') {
            return 'email';
        }

        if (dataType === 'url') {
            return 'url';
        }

        if (['number', 'integer', 'float', 'decimal', 'currency'].includes(dataType)) {
            return 'number';
        }

        if (dataType === 'date') {
            return 'date';
        }

        if (dataType === 'datetime') {
            return 'datetime-local';
        }

        return 'text';
    };

    const getFieldCategory = () => {
        if (!currentField) return 'text';
        const dataType = currentField.dataType.toLowerCase();

        if (['dropdown', 'multiselect', 'single-select', 'multiselect-checkbox'].includes(dataType)) {
            return 'list';
        }
        if (['boolean', 'checkbox'].includes(dataType)) {
            return 'boolean';
        }
        if (dataType === 'phone') {
            return 'phone';
        }
        if (dataType === 'object') {
            return 'object';
        }
        if (['number', 'integer', 'float', 'decimal', 'currency'].includes(dataType)) {
            return 'number';
        }
        if (['date', 'datetime'].includes(dataType)) {
            return 'date';
        }
        return 'text';
    };

    return {
        selectedField,
        selectedOperator,
        value,
        currentField,
        operators,
        loadingOperators,
        listOptions,
        loadingListValues,
        handleFieldChange,
        handleOperatorChange,
        handleClearField,
        handleClearOperator,
        handleClearValue,
        setValue,
        isValueRequired,
        getInputType,
        getFieldCategory
    };
};
